import { type SearchEntityType, type SearchResult, useSearch, useSearchHistory } from '@sazeo/sdk';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Dialog,
  DialogContent,
  cn,
} from '@sazeo/ui';
import {
  FileText,
  FolderOpen,
  Hash,
  History,
  type LucideIcon,
  MessageSquare,
  Search,
  Sparkles,
  User,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { tenantFunctionFetch } from '../lib/functionFetch';

export interface GlobalSearchProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const ENTITY_ORDER: SearchEntityType[] = [
  'user',
  'team',
  'document',
  'folder',
  'channel',
  'message',
];

const ENTITY_ICON: Record<SearchEntityType, LucideIcon> = {
  user: User,
  team: Users,
  document: FileText,
  folder: FolderOpen,
  channel: Hash,
  message: MessageSquare,
};

function iconFor(kind: SearchEntityType): LucideIcon {
  // noUncheckedIndexedAccess defeats the Record-is-total inference; fall back
  // to FileText so a future/unknown entity still renders an icon.
  return ENTITY_ICON[kind] ?? FileText;
}

function useGroupLabel() {
  const { t } = useTranslation('core.search');
  return useCallback(
    (kind: SearchEntityType): string => {
      switch (kind) {
        case 'user':
          return t('group_user');
        case 'team':
          return t('group_team');
        case 'document':
          return t('group_document');
        case 'folder':
          return t('group_folder');
        case 'channel':
          return t('group_channel');
        case 'message':
          return t('group_message');
        default:
          return '';
      }
    },
    [t],
  );
}

// ---------------------------------------------------------------------------
// Cmd+/ binding — mirrors packages/ui/src/patterns/useCommandBar.ts so the
// search overlay has its own independent shortcut without colliding with
// Cmd+K (AI / command bar). Hook is local to this component per spec §4.4.
// ---------------------------------------------------------------------------

export function useGlobalSearchShortcut(): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
} {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Cmd+/ on mac, Ctrl+/ on win/linux.
      if (event.key !== '/') return;
      if (!event.metaKey && !event.ctrlKey) return;
      event.preventDefault();
      setIsOpen((v) => !v);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return { isOpen, open, close, toggle };
}

// ---------------------------------------------------------------------------
// GlobalSearch — dialog overlay
// ---------------------------------------------------------------------------

interface ClaudeChatResponse {
  message: { role: string; content: string };
}

export function GlobalSearch({ isOpen, onOpenChange }: GlobalSearchProps) {
  const { t } = useTranslation('core.search');
  const { t: tAi } = useTranslation('core.cmdK');
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { data, isLoading } = useSearch(query);
  const { recent, push, clear } = useSearchHistory();
  const groupLabel = useGroupLabel();
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState('');

  // Reset transient state when the overlay closes.
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setAiResponse('');
      setAiLoading(false);
    }
  }, [isOpen]);

  const handleQueryChange = useCallback((nextQuery: string) => {
    setQuery(nextQuery);
    setAiResponse('');
    setAiLoading(false);
  }, []);

  const handleAskClaude = useCallback(
    async (q: string) => {
      if (!q.trim()) return;
      setAiLoading(true);
      setAiResponse('');
      try {
        const res = await tenantFunctionFetch('app/claude-chat', {
          method: 'POST',
          body: JSON.stringify({ messages: [{ role: 'user', content: q.trim() }] }),
        });
        const json = (await res.json()) as ClaudeChatResponse;
        setAiResponse(json.message?.content ?? '');
      } catch {
        setAiResponse(tAi('error'));
      } finally {
        setAiLoading(false);
      }
    },
    [tAi],
  );

  const grouped = useMemo(() => {
    const out: Array<{ kind: SearchEntityType; items: SearchResult[] }> = [];
    for (const kind of ENTITY_ORDER) {
      const list = data.grouped[kind];
      if (list && list.length > 0) out.push({ kind, items: list });
    }
    return out;
  }, [data]);

  const totalResults = grouped.reduce((acc, g) => acc + g.items.length, 0);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      push(query);
      onOpenChange(false);
      navigate(result.url);
    },
    [navigate, onOpenChange, push, query],
  );

  const handleSeeAll = useCallback(() => {
    const q = query.trim();
    if (!q) return;
    push(q);
    onOpenChange(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  }, [navigate, onOpenChange, push, query]);

  const handleRecent = useCallback((q: string) => {
    setQuery(q);
  }, []);

  const q = query.trim();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0">
        <Command
          shouldFilter={false}
          className={cn(
            '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium',
            '[&_[cmdk-group-heading]]:text-[var(--color-text-secondary)]',
            '[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0',
            '[&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:size-4',
            '[&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3',
            '[&_[cmdk-item]_svg]:size-4',
          )}
        >
          <CommandInput
            placeholder={t('placeholder')}
            value={query}
            onValueChange={handleQueryChange}
          />
          <CommandList>
            {q.length === 0 ? (
              recent.length === 0 ? (
                <CommandEmpty>{t('empty_state')}</CommandEmpty>
              ) : (
                <CommandGroup
                  heading={
                    <div className="flex w-full items-center justify-between">
                      <span>{t('recent_searches')}</span>
                      <button
                        type="button"
                        onClick={clear}
                        className="text-[11px] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]"
                      >
                        {t('clear_recent')}
                      </button>
                    </div>
                  }
                >
                  {recent.map((r) => (
                    <CommandItem key={r} value={`recent:${r}`} onSelect={() => handleRecent(r)}>
                      <History aria-hidden />
                      <span>{r}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )
            ) : isLoading ? (
              <div
                className="px-4 py-6 text-center text-[13px] text-[var(--color-text-secondary)]"
                aria-live="polite"
              >
                {t('searching')}
              </div>
            ) : totalResults === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-[13px] font-medium text-[var(--color-text-primary)]">
                  {t('no_results', { query: q })}
                </p>
                <p className="mt-1 text-[12px] text-[var(--color-text-tertiary)]">
                  {t('no_results_hint')}
                </p>
              </div>
            ) : (
              <>
                {grouped.map((group, idx) => {
                  const Icon = iconFor(group.kind);
                  return (
                    <div key={group.kind}>
                      {idx > 0 && <CommandSeparator />}
                      <CommandGroup heading={groupLabel(group.kind)}>
                        {group.items.map((result) => (
                          <CommandItem
                            key={`${group.kind}:${result.entity_id}`}
                            value={`${group.kind}:${result.entity_id}:${result.title}`}
                            onSelect={() => handleSelect(result)}
                          >
                            <Icon aria-hidden />
                            <div className="flex min-w-0 flex-1 flex-col">
                              <span className="truncate">{result.title}</span>
                              {(result.subtitle || result.body_snippet) && (
                                <span className="truncate text-[11px] text-[var(--color-text-tertiary)]">
                                  {result.body_snippet ?? result.subtitle}
                                </span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </div>
                  );
                })}
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem value="__see_all__" onSelect={handleSeeAll}>
                    <Search aria-hidden />
                    <span>{t('see_all_results')}</span>
                    <span className={cn('ml-auto text-[11px] text-[var(--color-text-tertiary)]')}>
                      {totalResults === 1
                        ? t('result_count_one', { count: totalResults })
                        : t('result_count_other', { count: totalResults })}
                    </span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
            {/* AI Query section — always visible when there is a non-empty query */}
            {q.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={tAi('ai_heading')}>
                  {!aiLoading && !aiResponse && (
                    <CommandItem
                      value={`ask-ai:${q}`}
                      onSelect={() => handleAskClaude(q)}
                      data-testid="cmdK-ask-ai-item"
                    >
                      <Sparkles size={16} aria-hidden />
                      <span>
                        {tAi('ask_ai')}: {q}
                      </span>
                    </CommandItem>
                  )}
                  {aiLoading && (
                    <div
                      data-testid="cmdK-ai-loading"
                      className="px-2 py-2 text-[13px] text-[var(--color-text-secondary)]"
                      aria-live="polite"
                    >
                      {tAi('ai_thinking')}
                    </div>
                  )}
                  {aiResponse && (
                    <div
                      data-testid="cmdK-ai-response"
                      className="px-2 py-2 text-[13px] text-[var(--color-text-primary)] max-w-[500px] whitespace-pre-wrap"
                      aria-live="polite"
                    >
                      {aiResponse}
                    </div>
                  )}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
